import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { Loader2, Search, Clock, Star, StarOff, Plus, AlertTriangle } from "lucide-react";

const RECENT_KEY = "tm_recent_symbols";
const FAV_KEY = "tm_fav_symbols";

function loadArr(key) { try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; } }
function saveArr(key, v) { localStorage.setItem(key, JSON.stringify(v.slice(0, 20))); }

export default function SymbolSearchDialog({ open, onClose, onPick }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState(loadArr(RECENT_KEY));
  const [favs, setFavs] = useState(loadArr(FAV_KEY));

  useEffect(() => {
    if (!open) { setQuery(""); setResults([]); return; }
    setRecent(loadArr(RECENT_KEY));
    setFavs(loadArr(FAV_KEY));
  }, [open]);

  useEffect(() => {
    const q = query.trim();
    if (!q || q.length < 1) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/market/search", { params: { q, limit: 12 } });
        setResults(data);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const pick = async (r) => {
    // enrich with quote
    let quote = null;
    try { quote = (await api.get(`/market/quote/${r.symbol}`)).data; } catch { /* noop */ }
    const enriched = { ...r, price: quote?.price, change_pct: quote?.change_pct };
    const newRecent = [enriched, ...recent.filter((x) => x.symbol !== r.symbol)].slice(0, 10);
    setRecent(newRecent); saveArr(RECENT_KEY, newRecent);
    onPick?.(enriched);
    onClose?.();
  };

  const toggleFav = (r, e) => {
    e.stopPropagation();
    const exists = favs.find((x) => x.symbol === r.symbol);
    const next = exists ? favs.filter((x) => x.symbol !== r.symbol) : [r, ...favs].slice(0, 20);
    setFavs(next); saveArr(FAV_KEY, next);
  };

  const list = query.trim().length ? results : [...favs, ...recent.filter(r => !favs.some(f => f.symbol === r.symbol))];
  const heading = query.trim().length ? "Results" : (favs.length ? "Favorites & Recent" : "Recent");

  return (
    <Dialog open={open} onOpenChange={(v)=>!v && onClose?.()}>
      <DialogContent className="max-w-2xl !rounded-none border-border/70" data-testid="symbol-search-dialog">
        <DialogHeader>
          <DialogTitle className="mono uppercase tracking-wider text-sm flex items-center gap-2">
            <Search size={14}/> Symbol Search
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Input
              autoFocus
              value={query}
              onChange={(e)=>setQuery(e.target.value)}
              placeholder="Search by company name or ticker (e.g., 'nvidia', 'space', 'AAPL')"
              className="!rounded-none h-11 mono text-sm pr-10"
              data-testid="symbol-search-input"
            />
            {loading && <Loader2 className="absolute right-3 top-3 animate-spin text-muted-foreground" size={16}/>}
          </div>

          <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            {query.trim().length === 0 && <Clock size={11}/>}
            {heading}
          </div>

          <div className="max-h-96 overflow-auto divide-y divide-border/40 border border-border/50">
            {list.length === 0 && (
              <div className="p-6 text-center text-xs text-muted-foreground">
                {query.trim() && !loading ? (
                  <span className="flex items-center justify-center gap-2"><AlertTriangle size={12}/> No matches for "{query}"</span>
                ) : "Type to search."}
              </div>
            )}
            {list.map((r) => {
              const isFav = favs.some((x) => x.symbol === r.symbol);
              return (
                <button
                  key={r.symbol}
                  onClick={()=>pick(r)}
                  data-testid={`symbol-result-${r.symbol}`}
                  className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-secondary/40"
                >
                  <div className="w-16 mono">{r.symbol}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{r.name}</div>
                    <div className="text-[10px] mono uppercase text-muted-foreground tracking-widest">
                      {r.exchange} · {r.quote_type} {r.sector ? `· ${r.sector}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {r.price != null && <div className="mono text-sm">${r.price.toFixed(2)}</div>}
                    {r.change_pct != null && <div className={`mono text-[10px] ${r.change_pct >= 0 ? "pnl-up" : "pnl-down"}`}>{r.change_pct >= 0 ? "+" : ""}{r.change_pct.toFixed(2)}%</div>}
                  </div>
                  <button
                    className="p-1 text-muted-foreground hover:text-signal-yellow"
                    onClick={(e)=>toggleFav(r, e)}
                    title={isFav ? "Unfavorite" : "Favorite"}
                  >{isFav ? <Star size={14} className="fill-signal-yellow text-signal-yellow"/> : <StarOff size={14}/>}</button>
                  <Plus size={14} className="text-muted-foreground"/>
                </button>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
